import { Close, WarningAmber } from "@mui/icons-material"
import { Alert, Box, Chip, Drawer, IconButton, Stack, Typography } from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import PayloadList from "./PayloadList"
import {
  DoorInspection,
  LockerReservationPairing,
} from "./saLockerDoorInspection"
import SaLockerDoorRepairPanel from "./SaLockerDoorRepairPanel"
import SaLockerProvenanceLine from "./SaLockerProvenanceLine"
import { apiVersionLabel, type ProvenanceDescriptor } from "./saLockerProvenance"

/**
 * One column of the comparison.
 *
 * No card, no border box, no inner table — columns are separated by whitespace
 * and a single heading rule. Three bordered cards each wrapping a bordered,
 * paginated table produced a wall of lines that squeezed the values out of
 * view; the task here is "does the left match the right", which needs contrast
 * BETWEEN columns, not decoration inside them. Each scrolls independently so a
 * long partner payload cannot push the SL2 column off-screen.
 */
const Column: FC<{ title: string; count?: number; children: React.ReactNode }> = ({
  title,
  count,
  children,
}) => (
  <Box
    sx={{
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      maxHeight: { lg: "calc(100vh - 190px)" },
    }}
  >
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ pb: 1, mb: 1.5, borderBottom: 1, borderColor: "divider", flexShrink: 0 }}
    >
      <Typography variant="subtitle2" fontWeight={600}>
        {title}
      </Typography>
      {count !== undefined && (
        <Typography variant="caption" color="text.secondary">
          {count}
        </Typography>
      )}
    </Stack>
    <Box sx={{ overflow: "auto", minHeight: 0, pr: 1 }}>{children}</Box>
  </Box>
)

const Empty: FC<{ label: string }> = ({ label }) => (
  <Typography variant="caption" color="text.disabled">
    {label}
  </Typography>
)

/**
 * Side-by-side inspection of one door: the partner's view and SL2's view.
 *
 * The value is in showing both sides unreconciled: when a door misbehaves the
 * answer is almost always that one side holds a reservation the other does not,
 * and the operator dashboards collapse exactly that into a single grey tile.
 *
 * ## No longer read-only by construction (LOCKER-343)
 *
 * This doc used to read *"Read-only by construction — it renders payloads, never
 * mutates."* That is no longer true and the change is deliberate rather than a
 * drift, so the claim is corrected here instead of being quietly left standing.
 *
 * What changed is confined to `SaLockerDoorRepairPanel`, mounted below for **v1
 * lockers only**, and it is still read-first: everything above the panel renders
 * payloads and nothing else, and the panel's own first press is a read. Writing
 * requires a second, explicit press — a third for the one irreversible class.
 *
 * Why here rather than a new locker-wide repair page: the drawer already computes
 * and displays the stranded signal (`hasOrphanSstReservation`), and it is already
 * reachable — the SA locker grid passes `alwaysClickable`, so a door that is inert
 * on the operator dashboard opens this. A separate surface would rebuild the
 * navigation and the diff table that this drawer already is.
 */
export const SaLockerDoorInspector: FC<{
  open: boolean
  onClose: () => void
  doorLabel: string
  apiVersion: "v1" | "v2"
  door: unknown
  inspection?: DoorInspection
  /**
   * Locker-wide pairing outcome, so an empty door can say WHY it is empty
   * rather than leaving "nothing is in this door" and "SL2 holds a row this
   * snapshot cannot address" looking identical on screen (LOCKER-355 §6).
   */
  pairing?: LockerReservationPairing
  /** Where each side's data came from — version + exact URL (LOCKER-355 §8). */
  provenance?: ProvenanceDescriptor[]
  /**
   * The partner's v1 locker id. Present only for v1 lockers, and its presence is
   * what enables the repair panel — the two endpoints key on it, and there is no
   * v2 equivalent because the v2 drawer has no hole to fill.
   */
  sstLockerId?: string
}> = ({
  open,
  onClose,
  doorLabel,
  apiVersion,
  door,
  inspection,
  pairing,
  provenance,
  sstLockerId,
}) => {
  const { t } = useTranslation()

  /**
   * Nothing on either side, and SL2 holds rows this snapshot cannot address.
   * Shown ONLY in that combination: an empty door with a clean locker really is
   * empty and must not be given a reason it does not have.
   */
  const unplaceable =
    (pairing?.unpairable.length ?? 0) + (pairing?.versionMismatched.length ?? 0)
  const showUnplaceableHint =
    !!inspection &&
    inspection.sl2Reservations.length === 0 &&
    inspection.sstReservations.length === 0 &&
    unplaceable > 0

  return (
    <Drawer data-ui="UI-SL2W-147"
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100vw", md: "94vw" },
          maxWidth: 1680,
          pt: "64px",
        },
      }}
    >
      <Stack direction="column" spacing={2} sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Typography variant="h6" fontWeight={600}>
              {t("saLockerConsole.door")} {doorLabel}
            </Typography>
            <Chip size="small" variant="outlined" label={apiVersionLabel(apiVersion)} />
          </Stack>
          <IconButton onClick={onClose} size="small" aria-label={t("close")}>
            <Close />
          </IconButton>
        </Stack>

        {!!provenance?.length && (
          <Stack direction="column" spacing={0.25}>
            {provenance.map((d) => (
              <SaLockerProvenanceLine
                key={`${d.apiVersion}:${d.labelKey}`}
                descriptor={d}
              />
            ))}
          </Stack>
        )}

        {showUnplaceableHint && (
          <Alert
            severity="info"
            variant="outlined"
            sx={{ fontSize: "0.8rem" }}
            data-testid="door-unplaceable-hint"
          >
            {t("saLockerConsole.unplaceableHint", { n: unplaceable })}
          </Alert>
        )}

        {inspection?.hasOrphanSstReservation && (
          <Alert
            icon={<WarningAmber fontSize="inherit" />}
            severity="warning"
            variant="outlined"
            sx={{ fontSize: "0.8rem" }}
          >
            {t("saLockerConsole.orphanSst")}
          </Alert>
        )}
        {inspection?.hasOrphanSl2Reservation && (
          <Alert
            icon={<WarningAmber fontSize="inherit" />}
            severity="warning"
            variant="outlined"
            sx={{ fontSize: "0.8rem" }}
          >
            {t("saLockerConsole.orphanSl2")}
          </Alert>
        )}

        {/* v1 ONLY. `apiVersion` is checked here as well as inside the panel so a
            future caller that forgets to withhold `sstLockerId` for a v2 locker still
            cannot surface a v1-only repair on a V2 door. */}
        {apiVersion === "v1" && (
          <SaLockerDoorRepairPanel
            // `key` INCLUDES THE DOOR ID ON PURPOSE — it is what discards a fetched
            // verdict (and an armed irreversible confirmation) when the drawer moves
            // to another door. The panel deliberately does not track that itself.
            key={`${sstLockerId ?? ""}:${(door as { door_id?: string } | null)?.door_id ?? ""}`}
            sstLockerId={sstLockerId}
            sstDoorId={(door as { door_id?: string } | null)?.door_id}
          />
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr 1fr" },
            columnGap: 4,
            rowGap: 3,
            alignItems: "start",
          }}
        >
          <Column title={t("saLockerConsole.doorPayload")}>
            <PayloadList data={door} />
          </Column>

          <Column
            title={t("saLockerConsole.sstReservation")}
            count={inspection?.sstReservations.length ?? 0}
          >
            {inspection?.sstReservations.length ? (
              <Stack direction="column" spacing={2.5}>
                {inspection.sstReservations.map((r, i) => (
                  <PayloadList key={i} data={r} />
                ))}
              </Stack>
            ) : (
              <Empty
                label={
                  apiVersion === "v1"
                    ? t("saLockerConsole.noSstReservationV1")
                    : t("saLockerConsole.noSstReservation")
                }
              />
            )}
          </Column>

          <Column
            title={t("saLockerConsole.sl2Reservation")}
            count={inspection?.sl2Reservations.length ?? 0}
          >
            {inspection?.sl2Reservations.length ? (
              <Stack direction="column" spacing={2.5}>
                {/* NO PER-ROW VERSION CHIP HERE, and its absence is deliberate
                    (review F1.1). Every row in `sl2Reservations` has already
                    passed `inspectDoor`'s same-version partition, so a row whose
                    version differs from the locker's can never reach this list —
                    a chip conditioned on that difference would be code that
                    cannot render, and a test for it could only assert a state
                    the pairing function does not produce.

                    A mixed locker is reported where its mixedness actually lives:
                    on the section header (`unit-mixed-versions`), from
                    `inspectLockerReservations`, which sees the whole locker
                    rather than one door. */}
                {inspection.sl2Reservations.map((r) => (
                  <Box key={r.id}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={(r as { status?: string }).status || "—"}
                        sx={{ height: 18, fontSize: "0.65rem" }}
                      />
                      <Typography
                        component={Link}
                        to={`/sa/reservations?id=${r.id}`}
                        variant="caption"
                        fontFamily="monospace"
                        color="primary"
                        sx={{ textDecoration: "none" }}
                      >
                        {r.id}
                      </Typography>
                    </Stack>
                    <PayloadList data={r} />
                  </Box>
                ))}
              </Stack>
            ) : (
              <Empty label={t("saLockerConsole.noSl2Reservation")} />
            )}
          </Column>
        </Box>
      </Stack>
    </Drawer>
  )
}

export default SaLockerDoorInspector
